import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { getCurrentAddressFromForm } from 'util/general';
import {
  HISTORY_REVIEW,
  HISTORY_RESIDENCE,
  HISTORY_EMPLOYMENT,
  HISTORY_EDUCATION,
} from 'config/formSections/history';
import {
  PSYCHOLOGICAL_DIAGNOSES,
} from 'config/formSections/psychological';
import { updateApplication, reportErrors } from '@redux/modules/application/actions';
import {
  selectHistoryFederalSection,
  selectMultipleCitizenshipRenounced,
  selectCitizenshipForeignPassportsSection,
  selectCitizenshipStatusMaidenName,
  selectCitizenshipStatusForm240Date,
  selectCitizenshipStatusForm240DateExplanation,
  selectFinancialBankruptcySection,
  selectFinancialBankruptcyTrusteeInformation,
  selectFinancialGamblingSection,
  selectFinancialTaxesSection,
  selectFinancialCardSection,
  selectFinancialCreditSection,
  selectFinancialDelinquentSection,
  selectFinancialDelinquentName,
  selectFinancialDelinquentNonFederal,
  selectFinancialFederalDebtSection,
  selectFinancialAlimonySection,
  selectFinancialAssetSeizedSection,
  selectFinancialChildSupportSection,
  selectFinancialCivilJudgementSection,
  selectFinancialEvictionSection,
  selectFinancialForeclosureSection,
  selectFinancialLienSection,
  selectFinancialRepossessionSection,
  selectFinancialNonpaymentNonfederalSection,
  selectFinancialNonpaymentSection,
  selectFinancialCardDisciplinaryDate,
  selectForeignContactsSection,
  selectForeignActivitiesSection,
  selectForeignBusinessSection,
  selectForeignTravelSection,
  selectForeignCounterIntelligence,
  selectForeignExcessiveKnowledge,
  selectForeignSensitiveInformation,
  selectForeignThreatened,
  selectLegalOtherOffensesSection,
  selectLegalNonCriminalCourtSection,
  selectLegalTechnologySection,
  selectLegalOffenseInvolvements,
  selectLegalOffenseSentenced,
  selectLegalOffenseIncarcerated,
  selectLegalInvestigationClearanceGranted,
  selectLegalPoliceFirearms,
  selectLegalPoliceDrugs,
  selectForeignMilitaryMaintainsContact,
  selectRelationshipMaritalForeignBornDocExpiration,
  selectRelationshipMaritalDivorcePhoneNumber,
  selectRelationshipRelativesForeignBornDoc,
  selectRelationshipRelativesUSResidenceDoc,
  selectRelationshipRelativesForeignGovtAffExplanation,
  selectDrugWhileSafetySection,
  selectDrugWithClearanceSection,
  selectDrugSupplementalSection,
  selectAlcoholSections,
  selectDrugWhileSafety,
  selectDrugWithClearance,
  selectDrugInFuture,
  selectAlcoholOrderedCounselingParty,
  selectAlcoholReceivedCounselingsSection,
  selectAlcoholSupplementalSection,
  selectHistoryResidenceReferenceInfo,
  selectHistoryResidenceRole,
  selectEmploymentHistorySupervisorEmail,
  selectEmploymentItemStatus,
  selectEmploymentItemLeft,
  selectEmploymentItemNonGovernment,
  selectEmploymentItemSelfEmploymentVerify,
  selectEmploymentSelfEmployedJobLocation,
  selectSupervisorAddress,
  selectSupervisorHasDifferentAddress,
  selectCitizenshipUsPassportExpiration,
  selectCitizenshipUsPassportName,
  selectIdentificationOtherNameExplanation,
  selectIdentificationContactInfo,
  selectCitizenshipStatusResidenceStatus,
  selectCitizenshipStatusAlienRegistrationExpiration,
  selectCitizenshipStatusNotCitizenDocumentFields,
  selectCitizenshipStatusNaturalizedFields,
  selectCitizenshipStatusCitizenshipCertificateFields,
  selectCitizenshipStatusForeignBorn,
  selectCitizenshipStatusDerived,
  selectHistoryResidenceReferenceName,
  selectHistoryEducationReferenceFields,
  selectRelationshipRelativesAdditionalFamily,
  selectRelationshipRelativesAPOFPOIDK,
  selectForeignContactAPOFPOIDK,
  selectRequireRelationshipMaritalSSN,
  selectRequireRelationshipCohabitantSSN,
} from 'selectors/branches';
import { selectValidUSPassport, selectRequiredDocumentsSF852013 } from 'selectors/misc';
import {
  hideDisciplinaryProceduresSelector,
  hideExistingConditionsSelector,
} from 'selectors/navigation';

import { totalYears, sort } from 'components/Section/History/helpers';
import { utc } from 'components/Section/History/dateranges';
import { rootSelectors } from '@redux/modules/selectors';
import DebugRender from 'components/Debug/Render/DebugRender';
import { ff } from 'helpers/featureFlags';
import { getDevSettings } from '@redux/modules/settings/selectors';
import { updateSubsectionRequest } from '@redux/modules/form/actions';
import { defaultReduxState } from 'reducers/testHelpers';
import { SubsectionContextType } from './types';

const initialContext: SubsectionContextType = {
  showRequiredErrors: false,
};

export const SubsectionContext = React.createContext(initialContext);

const connectSubsection = (Component, {
  key, section, subsection, store, displayErrorsReviewOnly = []
}) => {
  class ConnectedSubsection extends React.Component {
    constructor(props) {
      super(props);

      this.section = section;
      this.subsection = subsection;
      this.store = store;
    }

    shouldComponentUpdate(nextProps, nextState) {
      // This component is heavily reused throughout the application and depends
      // on state/routing. When the user refreshed the page, it caused all
      // components based on this type to re-render up to 80+ times.

      for (const key2 in nextProps) {
        if (this.props[key2] !== nextProps[key2]) {
          switch (key2) {
            // We may need to add other cases (to prevent wasted renders)
            case 'match':
            case 'suggestedNames':
            case 'gaps':
              if (JSON.stringify(this.props[key2]) !== JSON.stringify(nextProps[key2])) { return true; }
              break;
            case 'Birthdate':
              // handles date object
              if (this.props[key2] instanceof Date && nextProps[key2] instanceof Date) {
                if (this.props[key2].toString && nextProps[key2].toString) {
                  if (this.props[key2].toString() !== nextProps[key2].toString()) {
                    return true;
                  }
                }
              } else {
                return true;
              }

              break;
            case 'List':
              // JavaScript does not have a native way of handling deep object
              // comparison, and 'List' is a rather large object being passed
              // down. It may be best to look into third party libraries to help
              // here. In the meantime, we always return true (to prevent the
              // app from breaking) until a deep object comparison is available.
              return true;
            default:
              // In the scenario that we can't determine the difference between
              // the two objects, it is safer to return true. If we falsely
              // assume there is no difference, it'll prevent key components
              // from working correctly.
              return true;
          }
        }
      }

      return false;
    }

    /**
     * @returns {import('./types').SubsectionContextType}
     */
    buildContext = () => ({
      subsectionKey: key,
      section,
      subsection,
      store,
      showRequiredErrors:
        this.props.devSettings.showAllFieldErrors === true ? true : this.props.required ?? false,
      displayErrorsReviewOnly
    });

    handleError = (value, arr) => {
      if (ff(this.props.features).getActive('EN-4733')) {
        return [];
      }

      const action = reportErrors(this.section, this.subsection, arr);

      // This function is heavily ran throughout the application. When there are
      // no errors, it ended up updating the store and dispatch a lot of 'empty'
      // errors. This made it more difficult to troubleshoot.
      if (action.values.length === 0) { return arr; }

      // When the application hasn't initialized, this will report false
      // negative errors to the state. With the current implementation, it
      // wasn't clearing out when you were on the 'Review' pages, thus,
      // showcasing all sections were bad.
      if (this.props.hasInit === true) {
        this.props.dispatch(action);
      }

      return arr;
    };

    handleUpdate = (
      /**
       * The subsection to update values and run validation on.
       *
       * @deprecated This is only applicable for updateApplication.
       */
      field: string,

      /**
       * The values to update and validate
       */
      values: any,

      /**
       * (optional) array of affected subsections that should re-validate on this update
       */
      validateSubsections: string[] = []
    ) => {
      const { dispatch } = this.props;

      dispatch(updateApplication(this.store, field, values));
      dispatch(updateSubsectionRequest(key, values, validateSubsections));
    };

    render() {
      const { Birthdate } = this.props;
      const totalYearsProp = totalYears(Birthdate);

      return (
        <SubsectionContext.Provider value={this.buildContext()}>
          <DebugRender parent={this} />
          <Component
            onUpdate={this.handleUpdate}
            onError={this.handleError}
            totalYears={totalYearsProp}
            sort={sort}
            {...this.props}
          />
        </SubsectionContext.Provider>
      );
    }
  }

  ConnectedSubsection.propTypes = {
    dispatch: PropTypes.func,
    Birthdate: PropTypes.instanceOf(Date),
    hasInit: PropTypes.bool,
  };

  ConnectedSubsection.defaultProps = {
    dispatch: () => { },
    Birthdate: null,
  };

  const processDate = (date) => {
    if (!date) {
      return null;
    }

    let d = null;
    const { month, day, year } = date.Date;
    if (month && day && year) {
      d = utc(new Date(year, month - 1, day));
    }
    return d;
  };

  const extractSpouse = (marital) => {
    if (!marital || !marital.CivilUnion || !marital.CivilUnion.Name) {
      return null;
    }

    return marital.CivilUnion.Name;
  };

  /**
   *
   * @param {import('types/state').RootState} state
   */
  const mapStateToProps = (state) => {
    const { form = {}, application = {} } = state;

    const props = {}; // props to return

    // New section data & errors (state.form)
    const sectionData = form[key];

    // Special cases
    if (key === HISTORY_REVIEW.key) {
      props.errors = {
        [HISTORY_EDUCATION.key]: form[HISTORY_EDUCATION.key] && form[HISTORY_EDUCATION.key].errors,
        [HISTORY_EMPLOYMENT.key]:
          form[HISTORY_EMPLOYMENT.key] && form[HISTORY_EMPLOYMENT.key].errors,
        [HISTORY_RESIDENCE.key]: form[HISTORY_RESIDENCE.key] && form[HISTORY_RESIDENCE.key].errors,
      };
    }

    // Legacy form data (state.application)
    const {
      Identification = {}, History = {}, Settings = {}, Relationships = {}, Citizenship = {},
    } = application;

    const { formType, formVersion, hasInit } = Settings;
    const applicantName = rootSelectors.application.getApplicantName(state);
    // const names = rootSelectors.application.getOtherNames(state);
    const applicantBirthdate = rootSelectors.application.getApplicantBirthdate(state);
    const applicantSex = rootSelectors.application.getApplicantSex(state);
    const maritalStatus = rootSelectors.application.getMaritalStatus(state);
    const spouse = Relationships && extractSpouse(Relationships.Marital);
    const militaryHasServed = rootSelectors.application.getMilitaryHistoryHasServed(state);
    const peopleWhoKnowYou = rootSelectors.application.getPeopleWhoKnowYouWell(state);

    const {
      Education = { HasAttended: '', HasDegree10: '', List: { items: [] } },
      Residence = { List: { items: [] } },
      Employment = { List: { items: [] } },
    } = History;

    const AddressBooks = rootSelectors.application.getAddressBooks(state);
    try {
      return {
        ...props,

        // Section-specific data
        ...(sectionData && sectionData.data),
        ...sectionData,

        // General data
        Birthdate: processDate(Identification.ApplicantBirthDate),
        applicantBirthdate,
        applicantSex,
        addressBooks: AddressBooks,
        AddressBooks,
        applicantName,
        suggestedNames: rootSelectors.form.getApplicantSuggestedNames(state),
        formType,
        formVersion,
        hasInit,
        formErrors: [...(state.form[key]?.formErrors ?? [])],

        features: state.featureFlags,
        devSettings: getDevSettings(state),

        // History
        ...selectHistoryFederalSection(state),
        ...selectHistoryResidenceRole(state),
        ...selectHistoryResidenceReferenceInfo(state),
        ...selectEmploymentHistorySupervisorEmail(state),
        ...selectEmploymentItemStatus(state),
        ...selectEmploymentItemLeft(state),
        ...selectEmploymentItemNonGovernment(state),
        ...selectEmploymentItemSelfEmploymentVerify(state),
        ...selectEmploymentSelfEmployedJobLocation(state),
        ...selectSupervisorAddress(state),
        ...selectSupervisorHasDifferentAddress(state),
        ...selectHistoryResidenceReferenceName(state),
        ...selectHistoryEducationReferenceFields(state),
        Education,
        Residence,
        Employment,

        // Citizenship
        ...selectValidUSPassport(state),
        ...selectRequiredDocumentsSF852013(state),
        ...selectMultipleCitizenshipRenounced(state),
        ...selectCitizenshipForeignPassportsSection(state),
        ...selectCitizenshipStatusMaidenName(state),
        ...selectCitizenshipStatusForm240Date(state),
        ...selectCitizenshipStatusForm240DateExplanation(state),
        ...selectCitizenshipUsPassportExpiration(state),
        ...selectCitizenshipUsPassportName(state),
        ...selectCitizenshipStatusResidenceStatus(state),
        ...selectCitizenshipStatusAlienRegistrationExpiration(state),
        ...selectCitizenshipStatusNotCitizenDocumentFields(state),
        ...selectCitizenshipStatusNaturalizedFields(state),
        ...selectCitizenshipStatusCitizenshipCertificateFields(state),
        ...selectCitizenshipStatusForeignBorn(state),
        ...selectCitizenshipStatusDerived(state),
        Citizenship,

        // Financial
        ...selectFinancialDelinquentName(state),
        ...selectFinancialDelinquentNonFederal(state),
        ...selectFinancialBankruptcySection(state),
        ...selectFinancialBankruptcyTrusteeInformation(state),
        ...selectFinancialGamblingSection(state),
        ...selectFinancialTaxesSection(state),
        ...selectFinancialCardSection(state),
        ...selectFinancialCreditSection(state),
        ...selectFinancialDelinquentSection(state),
        ...selectFinancialNonpaymentSection(state),
        ...selectFinancialCardDisciplinaryDate(state),
        ...selectFinancialFederalDebtSection(state),
        ...selectFinancialAlimonySection(state),
        ...selectFinancialAssetSeizedSection(state),
        ...selectFinancialChildSupportSection(state),
        ...selectFinancialCivilJudgementSection(state),
        ...selectFinancialEvictionSection(state),
        ...selectFinancialForeclosureSection(state),
        ...selectFinancialLienSection(state),
        ...selectFinancialRepossessionSection(state),
        ...selectFinancialNonpaymentNonfederalSection(state),

        // Foreign
        ...selectForeignCounterIntelligence(state),
        ...selectForeignExcessiveKnowledge(state),
        ...selectForeignSensitiveInformation(state),
        ...selectForeignThreatened(state),
        ...selectForeignContactsSection(state),
        ...selectForeignActivitiesSection(state),
        ...selectForeignBusinessSection(state),
        ...selectForeignTravelSection(state),
        ...selectForeignContactAPOFPOIDK(state),

        // Legal
        ...selectLegalOffenseInvolvements(state),
        ...selectLegalOffenseSentenced(state),
        ...selectLegalOffenseIncarcerated(state),
        ...selectLegalPoliceFirearms(state),
        ...selectLegalPoliceDrugs(state),
        ...selectLegalInvestigationClearanceGranted(state),
        ...selectLegalOtherOffensesSection(state),
        ...selectLegalNonCriminalCourtSection(state),
        ...selectLegalTechnologySection(state),

        // Military
        ...selectForeignMilitaryMaintainsContact(state),
        showSelectiveService: true,
        showDisciplinaryProcedures: !hideDisciplinaryProceduresSelector(state),
        militaryHasServed,

        // Psychological
        showExistingConditions: !hideExistingConditionsSelector(state),

        // Relationships
        maritalStatus,
        peopleWhoKnowYou,
        spouse,
        currentAddress: getCurrentAddressFromForm(form),
        ...selectRelationshipMaritalForeignBornDocExpiration(state),
        ...selectRelationshipMaritalDivorcePhoneNumber(state),
        ...selectRequireRelationshipCohabitantSSN(state),
        ...selectRequireRelationshipMaritalSSN(state),
        ...selectRelationshipRelativesForeignBornDoc(state),
        ...selectRelationshipRelativesUSResidenceDoc(state),
        ...selectRelationshipRelativesForeignGovtAffExplanation(state),
        ...selectRelationshipRelativesAdditionalFamily(state),
        ...selectRelationshipRelativesAPOFPOIDK(state),

        // Substance use
        ...selectDrugWhileSafety(state),
        ...selectDrugWithClearance(state),
        ...selectDrugInFuture(state),
        ...selectAlcoholOrderedCounselingParty(state),
        ...selectAlcoholSections(state),
        ...selectAlcoholReceivedCounselingsSection(state),
        ...selectAlcoholSupplementalSection(state),
        ...selectDrugWhileSafetySection(state),
        ...selectDrugWithClearanceSection(state),
        ...selectDrugSupplementalSection(state),

        // Identification
        ...selectIdentificationOtherNameExplanation(state),
        ...selectIdentificationContactInfo(state),
      };
    } catch (e) {
      console.warn(`Unable to connect subsection ${key}`, e);
    }

    return {};
  };

  return connect(mapStateToProps)(ConnectedSubsection);
};

export default connectSubsection;
